<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="modal-style">
          <h3>{{ this.$t('components.LoversTable.faq.faq01.title') }}</h3>
          <p>{{ this.$t('components.LoversTable.faq.faq01.description') }}</p>

          <h3>{{ this.$t('components.LoversTable.faq.faq02.title') }}</h3>
          <p>{{ this.$t('components.LoversTable.faq.faq02.description') }}</p>

          <h3>{{ this.$t('components.LoversTable.faq.faq03.title') }}</h3>
          <p>{{ this.$t('components.LoversTable.faq.faq03.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template v-for="(modal, index) in cardOptionsModals">
      <DynamicModalComponent
        v-if="modal.props.value"
        :key="index"
        :component-name="modal.componentName"
        :custom-props="modal.props"
        :events="modal.events"
      />
    </template>

    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :items="items"
      :map-keys="mapKeys"
      :item-id="itemId"
      :open-dialog="openDialog"
      :has-custom-filter="true"
      :open-filter="openGenericFilterModal"
      class="make-a-copy"
      v-bind="$attrs"
      @reload="getData"
    />

    <v-data-table
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      :sort-by.sync="sortBy"
      :multi-sort.sync="multiSort"
      :sort-desc.sync="sortDesc"
      class="table-truncate"
    >
      <template v-slot:[`item.user_img_url`]="{ item }">
        <img
          v-if="item.user_img_url"
          :src="item.user_img_url"
          class="profile-picture"
          width="40px"
          @error="useWarroomAvatar"
        >
        <img
          v-else
          :src="avatarPlaceholder"
          class="profile-picture"
          width="40px"
        >
      </template>
      <template v-slot:[`item.polarity`]="{ item }">
        <span :style="{ color: getPolarityColor(item.polarity) }">{{ item.polarity }} %</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import GenericChart from './models/GenericChart.vue';
import DynamicModalComponent from '@/components/modal-model/DynamicModalComponent';
import { DsButton } from '@ds/components';
import colors from '@/utils/colors';
import EventBus from '@/utils/event-bus';
import loversHatersModel from '@/mixins/lovers-haters-model';

export default {
  name: 'LoversTable',
  components: {
    GenericChart,
    DsButton,
    DynamicModalComponent,
  },
  mixins: [
    loversHatersModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      items: [],
      itemsPerPage: 5,
      requestStatus: 'loading',
      filterDialog: false,
      dialog: false,
      sortBy: ['count'],
      multiSort: true,
      sortDesc: [true, true],
      avatarPlaceholder: 'https://warroom.stilingue.com.br/img/profiles/avatar.png',
      cardModalMethods: {
        openGenericFilterModal: this.openGenericFilterModalModal,
      },
      loversString: 'Lovers',
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('components.LoversTable.profilePicture'), value: 'user_img_url' },
        { text: this.$t('components.LoversTable.name'), value: 'name' },
        { text: this.$t('components.LoversTable.username'), value: 'username' },
        { text: this.$t('components.LoversTable.posts'), value: 'total_post' },
        { text: this.$t('components.LoversTable.comments'), value: 'total_comment' },
        { text: this.$t('components.LoversTable.inboxes'), value: 'total_inbox' },
        { text: this.$t('components.LoversTable.interactions'), value: 'count' },
        { text: this.$t('components.LoversTable.page'), value: 'page' },
        { text: this.$t('components.LoversTable.channel'), value: 'primary_channel' },
        { text: this.$t('components.LoversTable.polarity'), value: 'polarity' },
      ];
    },
    closeLabel() {
      return this.$t('components.LoversTable.closeLabel');
    },
    mapKeys() {
      return {
        name: this.$t('components.LoversTable.name'),
        username: this.$t('components.LoversTable.username'),
        count: this.$t('components.LoversTable.interactions'),
        total_comment: this.$t('components.LoversTable.comments'),
        total_inbox: this.$t('components.LoversTable.inboxes'),
        total_post: this.$t('components.LoversTable.posts'),
      };
    },
  },
  methods: {
    applyFilter(options) {
      this.items = [];
      this.requestStatus = 'loading';
      this.filterDialog = false;

      const promisses = this.getPromisses();

      Promise.all(promisses).then(async (responses) => {
        this.items = await this.aggregateResponses(responses);

        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }
        this.saveCards();
        this.requestStatus = 'success';
      }).catch((err) => {
        console.error(err);
        this.requestStatus = 'error';
      });
    },
    getPolarityColor(polarity) {
      if (polarity > 50) {
        return colors.ratingSatisfiedColor;
      } else if (polarity < 50) {
        return colors.ratingUnsatisfiedColor;
      }
      return colors.ratingNeutralColor;
    },
    useWarroomAvatar(event) {
      event.target.src = this.avatarPlaceholder;
    },
  },
};
</script>

<style lang="scss" scoped>

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

.profile-picture {
  border-radius: 50%;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }

  .close-icon {
    height: var(--ds-spacing-stack-lg);
    padding-top: var(--ds-spacing-stack-lg);
  }

  .v-dialog {
    overflow-x: hidden;
  }
}
</style>

